import { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useFormikContext } from 'formik'

import { getHoursOptions } from './helpers/hours.helpers'
import { LeadsValues, salesLeadsValues } from './LeadsPage.schema'

import { selectAgentId } from '~/agent/store/agent.selectors'
import { useAppDepsContext } from '~/core/hooks/useAppDepsContext'

export default function useLeadsPage() {
  const brandOptions = [
    {
      key: 'Yoigo',
      label: 'Yoigo',
      value: 'Yoigo',
    },
    {
      key: 'Masmovil',
      label: 'Masmovil',
      value: 'Masmovil',
    },
    {
      key: 'Pepephone',
      label: 'Pepephone',
      value: 'Pepephone',
    },
    {
      key: 'Llamaya',
      label: 'Llamaya',
      value: 'Llamaya',
    },
    {
      key: 'Lebara',
      label: 'Lebara',
      value: 'Lebara',
    },
    {
      key: 'Guuk',
      label: 'Guuk',
      value: 'Guuk',
    },
  ]

  const segmentOptions = [
    {
      key: 'Residencial',
      label: 'Residencial',
      value: 'residential',
    },
    {
      key: 'Negocios',
      label: 'Negocios',
      value: 'business',
    },
  ]

  const defaultProducts = [
    {
      key: 'Convergente',
      label: 'Convergente',
      value: 'convergent',
    },
    {
      key: 'Solo móvil',
      label: 'Solo móvil',
      value: 'only_mobile',
    },
    {
      key: 'MASFijo/ElFijo',
      label: 'MASFijo/ElFijo',
      value: 'mobile_fixed_line',
    },
    {
      key: 'Alarmas HomeGO',
      label: 'Alarmas HomeGO',
      value: 'security',
    },
    {
      key: 'MASMovil Alarma Lead',
      label: 'MASMovil Alarma Lead',
      value: 'security_mm',
    },
    {
      key: 'Seguro Pack Familiar DoctorGO',
      label: 'Seguro Pack Familiar DoctorGO',
      value: 'familyhealth',
    },
  ]

  const masmovilAndYoigoBusinessProductsOptions = [
    {
      key: 'e_invoice',
      label: 'Factura electrónica',
      value: 'e_invoice',
    },
    {
      key: 'cybersecurity',
      label: 'Ciberseguridad',
      value: 'cybersecurity',
    },
    {
      key: 'lite_digital_solutions',
      label: 'Soluciones Digitales Lite',
      value: 'lite_digital_solutions',
    },
    {
      key: 'online_presence_basic',
      label: 'Presencia Online BÁSICA (Servicio)',
      value: 'online_presence_basic',
    },
    {
      key: 'online_presence_basic_added',
      label: 'Presencia Online BÁSICA (Venta añadida de web, microsite y Ecommerce)',
      value: 'online_presence_basic_added',
    },
    {
      key: 'professional_web',
      label: 'Web Profesional',
      value: 'professional_web',
    },
    {
      key: 'google_visibility_essential',
      label: 'Visibilidad en Google gestionada ESENCIAL',
      value: 'google_visibility_essential',
    },
    {
      key: 'google_visibility_optimal',
      label: 'Visibilidad en Google gestionada ÓPTIMA',
      value: 'google_visibility_optimal',
    },
    {
      key: 'micrositee_web',
      label: 'Web Micrositee',
      value: 'micrositee_web',
    },
    {
      key: 'professional_ecommerce',
      label: 'Ecommerce profesional',
      value: 'professional_ecommerce',
    },
    {
      key: 'rrss_4',
      label: 'RRSS 4 (sin reseñas)',
      value: 'rrss_4nvergent',
    },
    {
      key: 'seo_4',
      label: 'SEO 4 Expresiones',
      value: 'seo_4',
    },
  ]

  const SECURITY = 'security'
  const CYBERSECURITY = 'cybersecurity'

  const hoursOptions = getHoursOptions()
  const { validateForm, setTouched, values, setValues } = useFormikContext<LeadsValues>()
  const { segment, brand } = values
  const [hasMsError, setHasMsError] = useState(false)
  const [hasMsSuccess, setHasMsSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { leadsService } = useAppDepsContext()
  const sfId = useSelector(selectAgentId)
  const productOptions = useMemo(() => {
    return segment === 'business' && (brand === 'Yoigo' || brand === 'Masmovil')
      ? [...defaultProducts, ...masmovilAndYoigoBusinessProductsOptions]
      : [...defaultProducts]
  }, [segment, brand])

  function sendSalesLead() {
    const { product, ...restValues } = values
    const body = {
      ...restValues,
      product: product.includes(SECURITY) && product !== CYBERSECURITY ? SECURITY : product,
      result: '',
      territoryOwner: '',
      technology: '',
      address: '',
      sfId,
    }
    setIsLoading(true)
    leadsService
      .setSalesLead(body)
      .then(() => {
        setIsLoading(false)
        setHasMsSuccess(true)
      })
      .catch(() => {
        setIsLoading(false)
        setHasMsError(true)
      })
  }

  async function onSubmit() {
    const errors = await validateForm()
    const hasErrors = !!Object.keys(errors).length

    const toTouched: { [key: string]: boolean } = {}

    Object.entries(errors).forEach(([key, value]) => {
      toTouched[key] = !!value
    })

    setTouched(toTouched)

    if (!hasErrors) {
      sendSalesLead()
    }
  }

  function onResetData() {
    setHasMsSuccess(false)
    setValues(salesLeadsValues)
  }

  return {
    onSubmit,
    onResetData,
    hasMsError,
    sendSalesLead,
    setHasMsError,
    hasMsSuccess,
    setHasMsSuccess,
    isLoading,
    brandOptions,
    productOptions,
    segmentOptions,
    hoursOptions,
    shouldShowHours:
      values.segment === 'residential' && (values.brand === 'Yoigo' || values.brand === 'Masmovil'),
  }
}
